// AddInvoiceItemFromProductModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { subscribeToNodesByDatabase } from 'services/nodes/NodeReadService';

// Utilities
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';

// Local
import {
  PRODUCT_TYPE_KEY,
  createInvoiceItemsFromProducts,
} from 'nodetypes/invoice/fromProduct/invoiceItemFromProduct';
import { INVOICE_ITEM_TYPE_KEY } from 'nodetypes/invoice/view/invoiceGeneralInfoModel';

// CSS
import './AddInvoiceItemFromProductModal.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import TextInput from 'components/textinput/TextInput';

function cleanString(value) {
  return String(value || '').trim();
}

function getNodeTitle(node) {
  return cleanString(node?.title) || 'Untitled';
}

function readProductSku(product) {
  const raw = readNodeFieldRawValue(
    product,
    { name: 'sku', source: 'product' },
    PRODUCT_TYPE_KEY,
  );
  return cleanString(raw);
}

/**
 * Modal: pick Products → create INVOICE_ITEM lines on a Invoice.
 */
const AddInvoiceItemFromProductModal = React.memo(({
  isOpen,
  onClose,
  invoiceNode = null,
  baseKey = '',
  user = null,
  onCreated = null,
}) => {
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const { getType } = useNodeTypes();

  const [filterTerm, setFilterTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const invoiceKey = cleanString(invoiceNode?.key);
  const invoiceTitle = getNodeTitle(invoiceNode) || 'this invoice';
  const itemType = useMemo(() => getType(INVOICE_ITEM_TYPE_KEY), [getType]);
  const selectedCount = selectedKeys.size;

  const filteredProducts = useMemo(() => {
    const needle = filterTerm.trim().toLowerCase();
    return (Array.isArray(products) ? products : [])
      .filter((product) => {
        if (!product?.key) return false;
        if (!needle) return true;
        const title = getNodeTitle(product).toLowerCase();
        const sku = readProductSku(product).toLowerCase();
        return title.includes(needle) || sku.includes(needle);
      })
      .sort((left, right) => getNodeTitle(left).localeCompare(getNodeTitle(right)));
  }, [filterTerm, products]);

  useEffect(() => {
    if (!isOpen) return;
    setFilterTerm('');
    setSelectedKeys(new Set());
    setBusy(false);
    setError('');
  }, [isOpen, invoiceKey]);

  useEffect(() => {
    if (!isOpen || !baseKey) {
      setProducts([]);
      return undefined;
    }
    return subscribeToNodesByDatabase(
      { type: PRODUCT_TYPE_KEY, baseKey },
      (rows) => setProducts(Array.isArray(rows) ? rows : []),
    );
  }, [isOpen, baseKey]);

  const handleToggle = useCallback((product) => {
    const key = cleanString(product?.key);
    if (!key || busy) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, [busy]);

  const handleConfirm = useCallback(async () => {
    if (!invoiceNode?.key || !user?.key || !selectedCount || busy) return;
    setBusy(true);
    setError('');
    try {
      const byKey = new Map(
        (Array.isArray(products) ? products : [])
          .filter((product) => product?.key)
          .map((product) => [cleanString(product.key), product]),
      );
      const toCreate = [...selectedKeys]
        .map((key) => byKey.get(key))
        .filter(Boolean);

      const created = await createInvoiceItemsFromProducts({
        products: toCreate,
        invoiceNode,
        user,
        baseKey: baseKey || invoiceNode.baseKey || null,
        itemType,
      });

      toast.success(
        `Added ${created.length} item${created.length === 1 ? '' : 's'}.`,
      );
      if (typeof onCreated === 'function') {
        onCreated(created);
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (err) {
      setError(cleanString(err?.message) || 'Could not create invoice items.');
    } finally {
      setBusy(false);
    }
  }, [
    baseKey,
    busy,
    itemType,
    onClose,
    onCreated,
    products,
    invoiceNode,
    selectedCount,
    selectedKeys,
    user,
  ]);

  const confirmLabel = busy
    ? 'Adding…'
    : `Add ${selectedCount || ''} item${selectedCount === 1 ? '' : 's'}`.trim();

  const emptyLabel = products.length === 0
    ? 'No products in this base yet. Add products to the library first.'
    : 'No products match that search.';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Items"
      width={isMobile ? '100%' : '520px'}
    >
      <ModalForm>
        <ModalHint>
          {`Choose library products to add as line items on ${invoiceTitle}.`}
        </ModalHint>

        <div className="add-item-from-product-modal">
          <ModalField label="Search">
            <div className="add-item-from-product-filter">
              <TextInput
                value={filterTerm}
                placeholder="Search products…"
                width="100%"
                onChange={(event) => setFilterTerm(event?.target?.value ?? '')}
              />
            </div>
          </ModalField>

          {error ? (
            <div className="add-item-from-product-error">{error}</div>
          ) : null}

          <div className="add-item-from-product-list">
            {filteredProducts.length === 0 ? (
              <div className="add-item-from-product-empty">
                {emptyLabel}
              </div>
            ) : (
              filteredProducts.map((product) => {
                const key = cleanString(product.key);
                const selected = selectedKeys.has(key);
                const sku = readProductSku(product);
                return (
                  <div
                    key={key}
                    className={`add-item-from-product-row${selected ? ' is-selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleToggle(product)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleToggle(product);
                      }
                    }}
                  >
                    <Checkbox value={selected} onChange={() => {}} />
                    <div className="add-item-from-product-row-body">
                      <div className="add-item-from-product-row-title">
                        {getNodeTitle(product)}
                      </div>
                      {sku ? (
                        <div className="add-item-from-product-row-meta">{sku}</div>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="right">
          <Button
            label="Cancel"
            backgroundColor="transparent"
            borderColor="transparent"
            foregroundColor={theme.foregroundMuted}
            onClick={onClose}
            disabled={busy}
          />
          <Button
            label={confirmLabel}
            onClick={handleConfirm}
            disabled={busy || selectedCount === 0}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

AddInvoiceItemFromProductModal.displayName = 'AddInvoiceItemFromProductModal';

export default AddInvoiceItemFromProductModal;
